import React, { useCallback, useEffect, useMemo, useState } from "react";

import CalendarHeatmap from "components/dashboard/calendar-heatmap/CalendarHeatmap";
import {
  AnalyticsResultDto,
  AnyFilterExpressionInput,
  Attribute,
  HeatData,
  MdmObjectDto,
  SortDirection,
  SortInput,
  useGetAnalyticsByObjectIdPagedLazyQuery,
  useGetAnalyticsByProjectIdPagedLazyQuery,
  useGetAnalyticsHeatmapByObjectIdLazyQuery,
  useGetAnalyticsHeatmapByProjectIdLazyQuery,
  useUpdateTableDetailsForUserMutation
} from "gql/generated/types";
import { ProgressSpinner } from "primereact/progressspinner";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { getFirstDayOfHalfYear, getLastDayOfCurrentMonthDate } from "utils/joda-time-utils";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter from "../hooks/useTableFilter";
import useUserTableDetails from "../hooks/useUserTableDetails";
import { initialFilter } from "../utils/const";
import { CustomRowData, MdmTableName, TableColumn } from "../utils/types";
import { errorToaster, renderCustomFieldValue, renderDeviceLink, renderObjectLinkInAnalytics, renderSeverity, searchAnalyticsFilter, successToaster } from "../utils/utils";
import LazyTable from "./LazyTable";
import TableColumnSelection from "./TableColumnSelection";

type ObjectAnalyticsProps = {
  object?: MdmObjectDto;
  tableName: MdmTableName;
};

const ObjectAnalytics: React.FC<ObjectAnalyticsProps> = ({ object, tableName }) => {
  const { projectId, userSettings, project } = useProjectContext();
  const [dateFrom, setDateFrom] = React.useState<Date>(getFirstDayOfHalfYear());
  const [dateTo, setDateTo] = React.useState<Date>(getLastDayOfCurrentMonthDate());
  const tableDetails = useUserTableDetails(userSettings, tableName);

  const columns: TableColumn<AnalyticsResultDto>[] = useMemo(
    () => [
      {
        field: "id.date",
        header: T("word.date"),
        sortable: true,
        body: rowData => parseToLocalZoneDateTime(rowData.id?.date).format(DEFAULT_DATE_FORMATTER),
        columnKey: "date",
      },
      {
        field: "severity",
        header: T("word.severity"),
        body: renderSeverity,
        sortable: true,
        columnKey: "severity",
      },
      {
        field: "id.analyticsType",
        header: T("word.analytics-type"),
        sortable: true,
        columnKey: "analyticType",
      },
      {
        field: "description",
        header: T("word.description"),
        sortable: true,
        columnKey: "description",
      },
      {
        field: "deviceType",
        header: T("word.device-role"),
        sortable: true,
        columnKey: "deviceType",
      },
      {
        field: "mdmDevice.identifier",
        header: T("word.device"),
        body: rowData => renderDeviceLink(rowData, projectId!),
        columnKey: "device",
      },
      {
        field: "mdmObject.title",
        header: T("word.object"),
        body: rowData => renderObjectLinkInAnalytics(rowData, projectId!),
        columnKey: "object",
        sortable: true,
        hidden: !!object,
      },
      {
        field: "mdmObject.address.fullAddress",
        header: T("word.address"),
        columnKey: "address",
        sortable: true,
        hidden: !!object,
      },
    ],
    [object, projectId]
  );

  const [customColumns, setCustomColumns] = useState<string[]>(() => {
    const columnList = [...(tableDetails?.columnOrders ?? [])].map(r => r!.name);
    if (userSettings && columnList.length > 0) {
      return columnList;
    }
    return columns?.map(col => col.columnKey) || [];
  });
  const [tableOptions, setTableOptions] = useTableFilter(tableName, {
    sortField: "id.date",
    reqSortField: "id.date",
    sortOrder: -1,
  });

  const [updateUserSettings, { loading: updateLoading }] = useUpdateTableDetailsForUserMutation({
    onCompleted: () => successToaster(T("alert.columns-saved-successfully")),
    onError: errorToaster,
  });
  const [getAnalyticsByObject, {data: analytics, loading}] = useGetAnalyticsByObjectIdPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  const [getAnalyticsByProject, {data, loading: loading2 }] = useGetAnalyticsByProjectIdPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  const getFilteredData = tableName === "Analytics" ? getAnalyticsByProject : getAnalyticsByObject
  const totalAnalytics =
    analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.pageInfo?.total ??
    data?.allAnalyticsResultsByProjectIdAndDateRangePaged.pageInfo?.total ??
    0;
  const isLoading = loading || loading2;
  const analyticsData = useMemo(
    () =>
      (analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.items ??
        data?.allAnalyticsResultsByProjectIdAndDateRangePaged.items ??
        []) as AnalyticsResultDto[],
    [analytics, data]
  );

  const fetchData = useCallback(
    async (
      take: number | null = null,
      skip: number | null = null,
      sort: SortInput[] | null = null,
      filters: AnyFilterExpressionInput[] | null = null,
      cacheAndNetwork: boolean = false,
      filter: AnyFilterExpressionInput | null = null
    ) => {
      const resp = await getFilteredData({
        variables: {
          input: {
            id: object?.id ?? projectId,
            range: {
              start: dateFrom,
              startInclusive: true,
              end: dateTo,
              endInclusive: true,
            },
            take: take ?? tableOptions.rows,
            skip: skip ?? tableOptions.first,
            sort: sort ?? [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: filters ?? tableOptions.andFilters,
            filter: filter ?? tableOptions.filter,
          },
        },
        fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
      });
      return resp.data;
    },
    [
      dateFrom,
      dateTo,
      getFilteredData,
      object?.id,
      projectId,
      tableOptions.andFilters,
      tableOptions.filter,
      tableOptions.first,
      tableOptions.reqSortField,
      tableOptions.rows,
      tableOptions.sortOrder,
    ]
  );

  useEffect(() => {
    if (userSettings) {
      fetchData(tableDetails?.rows);
    }
  }, [tableOptions, tableDetails?.rows, userSettings, fetchData]);

  const customCols: Attribute[] = useMemo(
    () =>
      tableName === "Analytics"
        ? ([
            ...(project?.mdmProjectById.configuration?.objectConfiguration.customFields ?? []),
            ...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? []),
          ] as Attribute[])
        : ([
            ...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? []),
          ] as Attribute[]),
    [
      project?.mdmProjectById.configuration?.deviceConfiguration.customFields,
      project?.mdmProjectById.configuration?.objectConfiguration.customFields,
      tableName,
    ]
  );

  const mapCols = useMemo(
    () => [
      ...columns,
      ...(customCols?.map(col => ({
        field: `${col?.id}.value`,
        header: T((col?.title ?? "") as StringKey),
        body: (rowData: CustomRowData) =>
          renderCustomFieldValue(rowData, col?.id as keyof CustomRowData, customCols),
        sortable: true,
        columnKey: col?.id,
      })) ?? []),
    ] as TableColumn<AnalyticsResultDto>[],
    [columns, customCols]
  );

  const formatObjectFields = useMemo(
    () =>
      analyticsData.reduce((acc, anal) => {
        const customFields =
          tableName === "Analytics"
            ? [...(anal.mdmObject?.customFields ?? []), ...(anal.mdmDevice?.customFields ?? [])]
            : anal.mdmDevice?.customFields;
        const objFields = customFields?.reduce((accObj, field) => {
          const newObj = {
            value: field?.value?.value,
            id: field?.key,
            title: customCols.find(col => col?.id === field?.key)?.title,
          };
          return {
            ...accObj,
            [field?.key!]: newObj,
          };
        }, {});

        acc.push({
          ...anal,
          ...objFields,
        });

        return acc;
      }, [] as any[]),
    [analyticsData, customCols, tableName]
  );

  const filteredColumns = useMemo(
    () => mapCols.filter(col => customColumns.includes(col.columnKey)),
    [customColumns, mapCols]
  );

  const handleColumnSave = () => {
    updateUserSettings({
      variables: {
        projectId: projectId!,
        tableDetails: {
          table: tableName,
          rows: tableOptions.rows,
          columnOrders: customColumns.map((col, i) => ({
            name: col,
            order: i,
          })),
        },
      },
    });
  };

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: "id.date",
      reqSortField: "id.date",
      sortOrder: -1,
    }));
  };

  const handleSearch = (value: string) => {
    setTableOptions(prev => ({
      ...prev,
      filter: value ? searchAnalyticsFilter(value) : null,
      first: 0,
      search: value,
    }));
  };

  return (
    <div className="mt-4">
      <CalendarHeatmapWrapper
        tableName={tableName}
        queryId={object?.id ?? projectId!}
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
      />
      <LazyTable
        columns={filteredColumns}
        data={formatObjectFields}
        tableName={tableName}
        buttons={
          <TableColumnSelection
            customColumns={customColumns}
            setCustomColumns={setCustomColumns}
            handleColumnSave={handleColumnSave}
            mapCol={mapCols}
            loading={updateLoading}
          />
        }
        containerName="AnalyticsResult"
        loading={isLoading}
        setTableOptions={setTableOptions}
        resetFilters={resetFilters}
        tableOptions={tableOptions}
        totalItems={totalAnalytics}
        fetchData={fetchData}
        onSearch={handleSearch}
      />
    </div>
  );
}

export default ObjectAnalytics;

type CalendarHeatmapWrapperProps = {
  tableName: MdmTableName;
  queryId: string;
  dateFrom: Date;
  dateTo: Date;
  setDateFrom: React.Dispatch<React.SetStateAction<Date>>;
  setDateTo: React.Dispatch<React.SetStateAction<Date>>;
}

const CalendarHeatmapWrapper: React.FC<CalendarHeatmapWrapperProps> = ({
  tableName,
  queryId,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
}) => {
  const [getHeatmapByObject, { data: objectHeatmap, loading }] =
    useGetAnalyticsHeatmapByObjectIdLazyQuery({
      onError: err => errorToaster(err),
    });

  const [getHeatmapByProject, { data: projectHeatmap, loading: loading2 }] =
    useGetAnalyticsHeatmapByProjectIdLazyQuery({
      onError: err => errorToaster(err),
    });

  const isLoading = loading || loading2;

  const getHeatmapData =
    tableName === "Analytics" ? getHeatmapByProject : getHeatmapByObject;

  const analyticsData: HeatData[] = useMemo(
    () => [
      ...(objectHeatmap?.analyticsHeatmapByObjectIdAndDateRange ??
        projectHeatmap?.analyticsHeatmapByProjectIdAndDateRange ??
        []),
    ],
    [objectHeatmap, projectHeatmap]
  );

  useEffect(() => {
    getHeatmapData({
      variables: {
        id: queryId,
        range: {
          start: dateFrom,
          startInclusive: true,
          end: dateTo,
          endInclusive: true,
        },
      },
    });
  }, [dateFrom, dateTo, getHeatmapData, queryId]);

  if (isLoading) return <ProgressSpinner/>

  const formatHeatData = analyticsData.map(data => ({
    value: data.value,
    date: new Date(data.date),
  }));

  return (
    <CalendarHeatmap
      startDate={dateFrom}
      endDate={dateTo}
      dataset={formatHeatData}
      setStartDate={setDateFrom}
      setEndDate={setDateTo}
      highValueColor="ed0000"
      lowValueColor="fde3e3"
      minValue={0}
      maxValue={100}
      staticMinMaxColors
      staticMinMaxTitles={false}
      staticMinMaxValues
      isCustomDataset
      showDateSelection
      ignoreCurrentDay={false}
    />
  );
};